/*
LIME LABS COMMERCE LAYER
Component: PDP Details Gate
Project: La Troupe
Version: 1.0
Status: LLCL-QA

Click-only derivative of the LLCL International Trust state model.
There is deliberately NO scroll listener, viewport trigger, resize trigger,
or automatic state release. Once the customer clicks, that state persists.
*/

(() => {
  'use strict';

  const VERSION = '1.0';
  const ROOT_SELECTOR = '[data-ll-pdp-details-gate]';
  const TARGET_SELECTOR = '.shopify-section--ll-pdp-v4-details';
  const MANAGER_KEY = 'LLPDPDetailsGateManager';

  const previous = window[MANAGER_KEY];
  if (previous?.version === VERSION) {
    previous.scan(document);
    return;
  }

  if (previous?.destroy) previous.destroy();

  class DetailsGateController {
    constructor(root) {
      this.root = root;
      this.toggle = root.querySelector('[data-ll-pdp-details-gate-toggle]');
      this.target = null;
      this.expanded = root.dataset.initialState !== 'collapsed';
      this.destroyed = false;
      this.onToggle = this.onToggle.bind(this);

      if (!this.toggle) return;

      this.toggle.addEventListener('click', this.onToggle);
      this.refreshTarget();
      this.setExpanded(this.expanded, 'initial');
      this.root.dataset.llPdpDetailsGateReady = 'true';
    }

    findTarget() {
      const owner = this.root.closest('.shopify-section--ll-pdp-details-gate');
      let candidate = owner?.nextElementSibling || null;

      while (candidate) {
        if (candidate.matches?.(TARGET_SELECTOR)) return candidate;
        candidate = candidate.nextElementSibling;
      }

      return document.querySelector(TARGET_SELECTOR);
    }

    clearTarget(target) {
      if (!target) return;
      target.classList.remove(
        'is-ll-pdp-details-managed',
        'is-ll-pdp-details-expanded',
        'is-ll-pdp-details-collapsed'
      );
      target.removeAttribute('aria-hidden');
      target.inert = false;
    }

    refreshTarget() {
      const nextTarget = this.findTarget();
      if (nextTarget === this.target) return;

      this.clearTarget(this.target);
      this.target = nextTarget;

      if (!this.target) return;

      this.target.classList.add('is-ll-pdp-details-managed');

      if (!this.target.id) {
        this.target.id = `ll-pdp-details-${Math.random().toString(36).slice(2, 9)}`;
      }

      this.toggle.setAttribute('aria-controls', this.target.id);
    }

    setExpanded(open, source = 'manual') {
      this.refreshTarget();
      const next = Boolean(open);

      if (!next && this.target?.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      this.expanded = next;
      this.root.classList.toggle('is-expanded', next);
      this.root.classList.toggle('is-collapsed', !next);
      this.root.dataset.motionState = next ? 'expanded' : 'collapsed';
      this.toggle.setAttribute('aria-expanded', next ? 'true' : 'false');

      if (this.target) {
        this.target.classList.add('is-ll-pdp-details-managed');
        this.target.classList.toggle('is-ll-pdp-details-expanded', next);
        this.target.classList.toggle('is-ll-pdp-details-collapsed', !next);
        this.target.setAttribute('aria-hidden', next ? 'false' : 'true');
        this.target.inert = !next;
      }

      this.root.dispatchEvent(new CustomEvent('ll:pdp-details-gate-state', {
        bubbles: true,
        detail: { state: next ? 'expanded' : 'collapsed', source }
      }));
    }

    onToggle(event) {
      event.preventDefault();
      this.setExpanded(!this.expanded, 'click');
    }

    refresh() {
      this.refreshTarget();
      this.setExpanded(this.expanded, 'refresh');
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.toggle?.removeEventListener('click', this.onToggle);
      this.clearTarget(this.target);
    }
  }

  const controllers = new Map();

  const prune = () => {
    controllers.forEach((controller, root) => {
      if (!root.isConnected) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  };

  const initialize = root => {
    if (!root || controllers.has(root)) return;
    controllers.set(root, new DetailsGateController(root));
  };

  const scan = (context = document) => {
    prune();
    if (context.matches?.(ROOT_SELECTOR)) initialize(context);
    context.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  const refreshAll = () => {
    requestAnimationFrame(() => {
      scan(document);
      controllers.forEach(controller => controller.refresh());
    });
  };

  const onSectionLoad = event => {
    scan(event.target || document);
    refreshAll();
  };

  const onSectionUnload = event => {
    controllers.forEach((controller, root) => {
      if (event.target === root || event.target?.contains?.(root)) {
        controller.destroy();
        controllers.delete(root);
      }
    });
    refreshAll();
  };

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);

  const manager = {
    version: VERSION,
    scan,
    refresh: refreshAll,
    open() {
      controllers.forEach(controller => controller.setExpanded(true, 'manager-open'));
    },
    close() {
      controllers.forEach(controller => controller.setExpanded(false, 'manager-close'));
    },
    destroy() {
      controllers.forEach(controller => controller.destroy());
      controllers.clear();
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    }
  };

  window[MANAGER_KEY] = manager;

  const start = () => scan(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
